import { useEffect, useState } from "react";
import { Trash2, Phone, Mail, MessageSquare, Car } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { INQUIRY_STATUSES } from "@/lib/format";
import { TID } from "@/constants/tids";

const STAT = {
  noua: { label: "Nouă", cls: "bg-[#D4AF37]/15 text-[#B5952F]" },
  contactata: { label: "Contactată", cls: "bg-blue-50 text-blue-700" },
  finalizata: { label: "Finalizată", cls: "bg-green-50 text-green-700" },
  respinsa: { label: "Respinsă", cls: "bg-red-50 text-red-600" },
};

const TYPE_LABEL = {
  contact: "Contact general",
  finantare: "Finanțare",
  masina: "Mașină",
};

export default function AdminInquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  const load = () => {
    const params = filter ? { status: filter } : {};
    api.get("/inquiries", { params }).then((r) => setItems(r.data));
  };

  useEffect(() => {
    const params = filter ? { status: filter } : {};
    api.get("/inquiries", { params }).then((r) => setItems(r.data));
  }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      toast.success("Status actualizat");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ștergi solicitarea?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      toast.success("Solicitare ștearsă");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-4 mb-6 sm:mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Inbox</div>
          <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-[#0A0A0A]">Solicitări</h1>
          <p className="mt-1 text-sm text-gray-500">{items.length} solicitări</p>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
          {[{ value: "", label: "Toate" }, ...INQUIRY_STATUSES].map((s) => (
            <button
              key={s.value}
              onClick={() => setFilter(s.value)}
              className={`h-10 px-4 rounded-full text-xs font-bold uppercase tracking-[0.14em] transition-colors shrink-0 ${
                filter === s.value
                  ? "bg-[#0A0A0A] text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300 active:bg-gray-50"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center text-sm text-gray-400">
            Nu există solicitări
            {filter ? " cu acest status." : "."}
          </div>
        )}
        {items.map((i) => (
          <div key={i.id} className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6">
            <div className="flex flex-col gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-base sm:text-lg font-bold text-[#0A0A0A] break-all">{i.name}</div>
                  <span
                    className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-full ${STAT[i.status]?.cls || ""}`}
                  >
                    {STAT[i.status]?.label || i.status}
                  </span>
                  <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                    {TYPE_LABEL[i.type] || i.type}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                  <a
                    href={`tel:${i.phone}`}
                    className="inline-flex items-center gap-2 hover:text-[#0A0A0A] active:text-[#0A0A0A] py-1"
                  >
                    <Phone className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
                    <span className="truncate">{i.phone}</span>
                  </a>
                  {i.email && (
                    <a
                      href={`mailto:${i.email}`}
                      className="inline-flex items-center gap-2 hover:text-[#0A0A0A] active:text-[#0A0A0A] py-1"
                    >
                      <Mail className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
                      <span className="truncate">{i.email}</span>
                    </a>
                  )}
                  {i.vehicle_label && (
                    <div className="inline-flex items-center gap-2">
                      <Car className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
                      <span className="truncate">{i.vehicle_label}</span>
                    </div>
                  )}
                  {i.period_months && (
                    <div className="text-gray-500">
                      Perioadă: <b className="text-[#0A0A0A]">{i.period_months} luni</b>
                      {i.advance ? ` · Avans: € ${i.advance}` : ""}
                    </div>
                  )}
                </div>
                {i.message && (
                  <div className="mt-3 flex items-start gap-2 text-sm text-gray-600 bg-[#F8F9FA] rounded-xl p-3">
                    <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                    <span className="whitespace-pre-line break-words">{i.message}</span>
                  </div>
                )}
                <div className="mt-3 text-xs text-gray-400">
                  {new Date(i.created_at).toLocaleString("ro-RO")}
                </div>
              </div>
              <div className="flex items-center gap-2 border-t border-gray-100 pt-3 sm:border-t-0 sm:pt-0">
                <select
                  data-testid={TID.adminInquiryStatus}
                  value={i.status}
                  onChange={(e) => updateStatus(i.id, e.target.value)}
                  className="flex-1 bg-white border border-gray-200 rounded-xl h-11 px-3 text-sm font-semibold outline-none focus:border-[#D4AF37]"
                >
                  {INQUIRY_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => remove(i.id)}
                  className="h-11 w-11 rounded-lg hover:bg-red-50 active:bg-red-100 flex items-center justify-center text-red-600 shrink-0"
                  title="Șterge"
                  aria-label="Șterge"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
